import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Battery, Compass, Gauge, Sun, Brain, Clock, Thermometer, Cloud, Pill, Smartphone } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { riseFade } from "@/lib/motion";
import PillarSeo from "@/components/seo/PillarSeo";
import ShareButtons from "@/components/blog/ShareButtons";
import BookmarkButton from "@/components/blog/BookmarkButton";
import RelatedArticles from "@/components/blog/RelatedArticles";

const META_TITLE = "MS Energy Management: The Complete Guide (2026)";
const META_DESC =
  "The complete guide to MS energy management: pacing, tracking, budgeting, heat, sleep, medications. 10 in-depth guides for people living with multiple sclerosis fatigue.";

const FAQS = [
  {
    question: "What is MS energy management?",
    answer:
      "MS energy management is the practice of predicting, protecting, and spending your daily energy on purpose, using pacing, tracking, and environmental controls, so multiple sclerosis fatigue costs you fewer plans, decisions, and relationships over time.",
  },
  {
    question: "What's the difference between pacing and energy budgeting?",
    answer:
      "Pacing is the technique, breaking tasks into shorter blocks with planned rest. Energy budgeting is the framework, deciding in advance what you'll spend today's energy on, so pacing serves your priorities instead of just spreading fatigue evenly across the day.",
  },
  {
    question: "Does energy management actually change outcomes in MS?",
    answer:
      "Peer-reviewed occupational therapy trials show energy conservation programs meaningfully reduce fatigue impact and improve quality of life in MS. It won't cure fatigue, but it consistently reduces how much life fatigue steals.",
  },
  {
    question: "Where should I start if I've never tracked before?",
    answer:
      "Start with two weeks of a 60-second daily check-in, energy, sleep, and one stressor. Don't change anything yet. The point of the first two weeks is to see your pattern, not to fix it.",
  },
  {
    question: "Can I manage MS energy without a wearable?",
    answer:
      "Yes. A daily check-in is the core signal. Wearables (Oura, Apple Watch, Fitbit, Garmin) sharpen the picture by adding HRV and sleep architecture, but the framework works entirely on self-report.",
  },
];

const KEEPERS = [
  {
    slug: "ms-fatigue-management-complete-guide",
    title: "MS Fatigue Management: The Complete Guide",
    blurb: "The full playbook, what MS fatigue is, why it happens, and every proven strategy in one place.",
    icon: Compass,
  },
  {
    slug: "what-does-ms-fatigue-feel-like",
    title: "What Does MS Fatigue Actually Feel Like?",
    blurb: "The felt experience of MS fatigue in the words of the people living with it, and why 'tired' is the wrong word.",
    icon: Battery,
  },
  {
    slug: "how-long-does-ms-fatigue-last",
    title: "How Long Does MS Fatigue Last?",
    blurb: "Hours, days, months, forever? What the data says about episode length, waves, and the long-term arc.",
    icon: Clock,
  },
  {
    slug: "does-ms-fatigue-ever-go-away",
    title: "Does MS Fatigue Ever Go Away?",
    blurb: "The honest answer, plus what people who feel meaningfully better did to get there.",
    icon: Sun,
  },
  {
    slug: "best-medication-for-ms-fatigue",
    title: "The Best Medications for MS Fatigue",
    blurb: "Modafinil, amantadine, methylphenidate, off-label options, what works, what doesn't, and what to ask your neurologist.",
    icon: Pill,
  },
  {
    slug: "brain-fog-ms-cognitive-fatigue",
    title: "MS Brain Fog & Cognitive Fatigue",
    blurb: "The cognitive tax nobody warned you about, and how to spend less on thinking so you have more left for living.",
    icon: Brain,
  },
  {
    slug: "ms-heat-sensitivity-energy",
    title: "MS Heat Intolerance & Uhthoff's Phenomenon",
    blurb: "Why a warm room can flatten you, and the cooling protocols that actually protect your day.",
    icon: Thermometer,
  },
  {
    slug: "weather-changes-ms-fatigue",
    title: "Barometric Pressure & MS Fatigue",
    blurb: "How to read barometric pressure as one signal in your pattern, so you can plan the day around it with intention.",
    icon: Cloud,
  },
  {
    slug: "spoon-theory-vs-energy-budgeting-ms",
    title: "Spoon Theory vs. Energy Budgeting",
    blurb: "Why spoons are a great metaphor but a lousy planning tool, and what to use instead.",
    icon: Gauge,
  },
  {
    slug: "best-ms-tracking-apps-2026",
    title: "The Best MS Tracking Apps (2026)",
    blurb: "The honest comparison of every MS-specific and general fatigue tracker, what to pick, what to skip.",
    icon: Smartphone,
  },
];

const EnergyManagementMs = () => {
  return (
    <>
      <PillarSeo
        slug="blog/pillar/energy-management-ms"
        title={META_TITLE}
        description={META_DESC}
        faqs={FAQS}
        date="2026-07-15"
      />
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-32 pb-20">
          {/* Header */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-muted-foreground hover:text-primary transition-colors mb-10"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to library
            </Link>
            <motion.div initial="hidden" animate="visible" variants={riseFade} className="space-y-6">
              <div className="eyebrow">Pillar guide · Energy management</div>
              <h1 className="font-display text-foreground text-display-xl leading-[1.05]">
                MS energy management,{" "}
                <span className="italic text-primary">the complete guide</span>.
              </h1>
              <p className="text-lg leading-[1.7] text-muted-foreground max-w-2xl">
                Everything we know about predicting, protecting, and spending the energy you have
                when you live with multiple sclerosis. Ten in-depth guides, one framework, no
                magical thinking.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link to="/demo">
                  <Button variant="premium" size="lg" className="group w-full sm:w-auto px-8">
                    See the demo
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <a href="#guides">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-8">
                    Jump to the guides
                  </Button>
                </a>
              </div>
              <ShareButtons slug="blog/pillar/energy-management-ms" title={META_TITLE} summary={META_DESC} className="justify-center mt-6" />
              <div className="flex justify-center mt-3">
                <BookmarkButton slug="blog/pillar/energy-management-ms" title="Energy Management with MS" category="Pillar" kind="pillar" />
              </div>
            </motion.div>
            <div className="hairline-fade mt-12" />
          </section>

          {/* What this pillar covers */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl pt-16">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-6">
              The framework in three moves
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.75] font-light text-lg">
              <p>
                Most MS fatigue advice is one of two things: a list of tips ("nap, hydrate, cool
                down") or a metaphor ("spoons"). Neither survives contact with a real week. What
                you actually need is a framework you can run every day, in under a minute, that
                turns yesterday's data into tomorrow's plan.
              </p>
              <p>
                <strong>Predict.</strong> Know what tomorrow is likely to cost you before it
                arrives. See{" "}
                <Link to="/blog/what-does-ms-fatigue-feel-like">what MS fatigue actually feels like</Link>,{" "}
                <Link to="/blog/how-long-does-ms-fatigue-last">how long episodes typically last</Link>, and{" "}
                <Link to="/blog/does-ms-fatigue-ever-go-away">what the long-term arc looks like</Link>.
              </p>
              <p>
                <strong>Protect.</strong> Remove the environmental and cognitive costs you don't
                need to be paying. Start with{" "}
                <Link to="/blog/ms-heat-sensitivity-energy">heat intolerance</Link>,{" "}
                <Link to="/blog/weather-changes-ms-fatigue">how barometric pressure shows up in your pattern</Link>, and{" "}
                <Link to="/blog/brain-fog-ms-cognitive-fatigue">cognitive fatigue</Link>. Add{" "}
                <Link to="/blog/best-medication-for-ms-fatigue">medication options</Link> in conversation with your neurologist.
              </p>
              <p>
                <strong>Spend on purpose.</strong> Choose what today's energy is for, using{" "}
                <Link to="/blog/spoon-theory-vs-energy-budgeting-ms">energy budgeting instead of spoon theory</Link>,
                and let a{" "}
                <Link to="/blog/best-ms-tracking-apps-2026">tracking app</Link>{" "}
                (or the daily check-in below) do the pattern-finding for you. If you want the full
                methodology in one place, read the{" "}
                <Link to="/blog/ms-fatigue-management-complete-guide">complete guide to MS fatigue management</Link>.
                For the symptom itself, what MS fatigue is, how it feels, and what helps, see the companion{" "}
                <Link to="/blog/pillar/ms-fatigue">MS fatigue pillar</Link>.
              </p>
            </div>
          </section>

          {/* Keeper index */}
          <section id="guides" className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-5xl pt-20">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="eyebrow justify-center mx-auto mb-4">The library</div>
              <h2 className="font-display text-3xl sm:text-4xl text-foreground">
                The 10 guides that make up this framework
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                Read in any order. Each one links back here.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {KEEPERS.map(({ slug, title, blurb, icon: Icon }, i) => (
                <motion.div
                  key={slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                >
                  <Link to={`/blog/${slug}`} className="group block h-full">
                    <Card className="h-full p-6 transition-all duration-300 hover:shadow-[0_20px_60px_-30px_hsl(var(--foreground)/0.25)] hover:-translate-y-0.5">
                      <div className="flex items-start gap-4">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/8 text-primary flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-display text-xl text-foreground leading-snug group-hover:text-primary transition-colors">
                            {title}
                          </h3>
                          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{blurb}</p>
                          <span className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-primary/80 group-hover:text-primary">
                            Read guide <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-20 max-w-3xl" aria-labelledby="pillar-em-faq">
            <h2 id="pillar-em-faq" className="font-display text-3xl sm:text-4xl text-foreground text-center mb-10">
              Frequently asked questions
            </h2>
            <div className="space-y-8">
              {FAQS.map((f) => (
                <div key={f.question}>
                  <h3 className="font-display text-xl text-foreground mb-2">{f.question}</h3>
                  <p className="text-foreground/80 leading-[1.75] font-light">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <RelatedArticles topics={["energy management", "pacing", "spoon theory", "energy budgeting"]} featuredSlugs={["spoon-theory-vs-energy-budgeting-ms", "best-ms-tracking-apps-2026", "ms-heat-sensitivity-energy"]} />
          {/* CTA */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
            <Card className="p-10 text-center bg-primary/5 border-primary/10">
              <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-4">
                Turn this framework into a daily habit
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                A 60-second morning check-in becomes a one-sentence forecast for the day ahead.
                By invitation. No wearable required.
              </p>
              <Link to="/demo">
                <Button variant="premium" size="lg" className="group px-8">
                  See the demo
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </Card>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default EnergyManagementMs;
